import type {
  ConfigResponse,
  AccessStatus,
  CreativeAgentManifest,
  CreativeAgentRequest,
  DashboardResponse,
  GenerationRequest,
  GenerationRequestPayload,
  ImportResponse,
  ProductDetailResponse,
  ProductImageRegistry,
  ReviewsResponse,
  ReviewUpdatePayload,
  RunResponse,
  SaveScriptRevisionPayload,
  SaveScriptRevisionResponse,
  ValidationResponse,
  VersionControlSnapshot,
} from "./types";

let accessCsrf = "";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const base = import.meta.env.BASE_URL.endsWith("/")
    ? import.meta.env.BASE_URL
    : `${import.meta.env.BASE_URL}/`;
  const response = await fetch(`${base}${path.replace(/^\//, "")}`, {
    headers: {
      "Content-Type": "application/json",
      ...(accessCsrf ? { "X-CSRF-Token": accessCsrf } : {}),
      ...init?.headers,
    },
    ...init,
  });
  const data: unknown = await response.json();
  if (!response.ok) {
    const message =
      isRecord(data) && typeof data.error === "string"
        ? data.error
        : `请求失败（HTTP ${response.status}）`;
    throw new Error(message);
  }
  return data as T;
}

export const api = {
  getDashboard: () => request<DashboardResponse>("/api/dashboard"),
  getProduct: (productId: string) =>
    request<ProductDetailResponse>(
      `/api/products/${encodeURIComponent(productId)}`,
    ),
  getReviews: () => request<ReviewsResponse>("/api/reviews"),
  getConfig: () => request<ConfigResponse>("/api/config"),
  getAccessStatus: async () => {
    const value = await request<AccessStatus>("/api/access/status");
    accessCsrf = value.csrf || "";
    return value;
  },
  unlockAccess: async (accessCode: string) => {
    const value = await request<{ unlocked: boolean; csrf: string; expires_at: string }>(
      "/api/access/unlock",
      { method: "POST", body: JSON.stringify({ access_code: accessCode }) },
    );
    accessCsrf = value.csrf || "";
    return value;
  },
  resetDemo: () =>
    request<{ reset: boolean; message: string }>("/api/demo/reset", {
      method: "POST",
      body: JSON.stringify({}),
    }),
  validateCsv: (inputPath: string) =>
    request<ValidationResponse>("/api/validate", {
      method: "POST",
      body: JSON.stringify({ input_path: inputPath }),
    }),
  importCsv: (filename: string, content: string) =>
    request<ImportResponse>("/api/import", {
      method: "POST",
      body: JSON.stringify({ filename, content }),
    }),
  runWorkflow: (inputPath: string, limit: number | null) =>
    request<RunResponse>("/api/run", {
      method: "POST",
      body: JSON.stringify({
        input_path: inputPath,
        output_path: "outputs",
        limit,
      }),
    }),
  updateReview: (payload: ReviewUpdatePayload) =>
    request<ReviewUpdatePayload>("/api/reviews", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  createXiaoyunqueTask: (payload: CreativeAgentRequest) =>
    request<CreativeAgentManifest>("/api/creative-agent/xiaoyunque", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  refreshXiaoyunqueTask: (productId: string) =>
    request<CreativeAgentManifest>(
      "/api/creative-agent/xiaoyunque/refresh",
      {
        method: "POST",
        body: JSON.stringify({ product_id: productId }),
      },
    ),
  saveScriptRevision: (
    productId: string,
    payload: SaveScriptRevisionPayload,
  ) =>
    request<SaveScriptRevisionResponse>(
      `/api/products/${encodeURIComponent(productId)}/script-revisions`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    ),
  selectFinalVideoVersion: (
    productId: string,
    videoVersionId: string,
  ) =>
    request<VersionControlSnapshot>(
      `/api/products/${encodeURIComponent(productId)}/video-versions/${encodeURIComponent(videoVersionId)}/select-final`,
      {
        method: "POST",
        body: JSON.stringify({}),
      },
    ),
  createGenerationPreview: (
    productId: string,
    payload: GenerationRequestPayload,
  ) =>
    request<GenerationRequest>(
      `/api/products/${encodeURIComponent(productId)}/generation-previews`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    ),
  submitGenerationRequest: (
    productId: string,
    payload: GenerationRequestPayload,
  ) =>
    request<GenerationRequest>(
      `/api/products/${encodeURIComponent(productId)}/generation-requests`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    ),
  reconcileGenerationRequest: (requestId: string) =>
    request<GenerationRequest>(
      `/api/generation-requests/${encodeURIComponent(requestId)}/reconcile`,
      {
        method: "POST",
        body: JSON.stringify({}),
      },
    ),
  uploadProductImage: (
    productId: string,
    filename: string,
    contentBase64: string,
  ) =>
    request<ProductImageRegistry>(
      `/api/products/${encodeURIComponent(productId)}/product-images`,
      {
        method: "POST",
        body: JSON.stringify({
          filename,
          content_base64: contentBase64,
        }),
      },
    ),
  selectProductImage: (
    productId: string,
    assetId: string,
  ) =>
    request<ProductImageRegistry>(
      `/api/products/${encodeURIComponent(productId)}/product-images/${encodeURIComponent(assetId)}/select`,
      {
        method: "POST",
        body: JSON.stringify({}),
      },
    ),
  importPerformanceCsv: (filename: string, content: string) =>
    request<{ imported_count: number; duplicate_count: number }>("/api/performance/import", { method: "POST", body: JSON.stringify({ filename, content }) }),
  setPerformanceExampleApproval: (metricId: string, approved: boolean) =>
    request<{ metric_id: string; approved_example: boolean }>(`/api/performance/${encodeURIComponent(metricId)}/approve`, { method: "POST", body: JSON.stringify({ approved }) }),
};
